import * as React from 'react';

import { Button, ConsoleDialog } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { itemAliasCreateAtom } from '../../../_graphql/mutationItemAliasCreate';
import {
  $canSubmitItemAliasForm,
  $itemAliasForm,
  $resetItemAliasForm,
} from '../../../_state/itemAliasForm.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { ItemAliasFormFields } from './ItemAliasFormFields';

interface ItemAliasCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ItemAliasCreateDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: ItemAliasCreateDialogProps) => {
  const t = useTranslations();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useAtomValue($itemAliasForm);
  const canSubmit = useAtomValue($canSubmitItemAliasForm);

  const resetForm = useSetAtom($resetItemAliasForm);
  const createItemAlias = useSetAtom(itemAliasCreateAtom({}));

  const toast = useToast();

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    if (!form.itemId || !form.accountId) {
      return;
    }

    try {
      setIsSubmitting(true);
      await createItemAlias({
        itemId: form.itemId,
        accountId: form.accountId,
        aliasName: form.aliasName.trim(),
        aliasCode: form.aliasCode.trim() || undefined,
        aliasType: form.aliasType,
        notes: form.notes.trim() || undefined,
      });
      toast.success(t.inventoryView.itemAliasCreateDialog.messages.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.inventoryView.itemAliasCreateDialog.messages.error(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [createItemAlias, form, toast, onSuccess, handleClose, t]);

  return (
    <ConsoleDialog
      className="@ItemAliasCreateDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.inventoryView.itemAliasCreateDialog.title}
      content={<ItemAliasFormFields disabled={isSubmitting} showSelectors />}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.inventoryView.itemAliasCreateDialog.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit || isSubmitting}
          >
            {isSubmitting
              ? t.inventoryView.itemAliasCreateDialog.actions.creating
              : t.inventoryView.itemAliasCreateDialog.actions.create}
          </Button>
        </div>
      }
    />
  );
};
